import { decodeJwt } from './../../helpers/helpers';
import { GoogleUser, SignInUser, User } from 'pages/Auth/auth.types';

import { createAsyncThunk } from '@reduxjs/toolkit';

import { ErrorToast, SuccessToast } from 'components/Toasts/Toasts';
import { setTokensToLS, setUserToLS } from 'helpers/localstorage-helper';
import {
  getUserAsync,
  sendResetPasswordEmailAsync,
  signInAsync,
  signInGoogleAsync,
  signInGoogleCompleteAsync,
  signUpAsync,
  updateUserProfileAsync,
} from 'requests/auth.request';
import axios from 'axios';
import { RootState } from 'app/store';

export const signUp = createAsyncThunk(
  'auth/signUp',
  async (
    { firstName, lastName, username, email, country, password, navigate }: User,
    { rejectWithValue }
  ) => {
    try {
      const { data } = await signUpAsync({
        firstName,
        lastName,
        username,
        email,
        country: country.label,
        countryCode: country.value,
        password,
      });

      setUserToLS(data.user);
      setTokensToLS(data.tokens);
      navigate('/');

      return data;
    } catch (e: any) {
      ErrorToast(e.response.data.message);
      return rejectWithValue(e.response.data.error);
    }
  }
);

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ email, password, navigate }: SignInUser, { rejectWithValue }) => {
    try {
      const { data } = await signInAsync({
        email,
        password,
      });

      setUserToLS(data.user);
      setTokensToLS(data.tokens);
      navigate('/');
      return data;
    } catch (e: any) {
      ErrorToast(e.response.data.message);
      return rejectWithValue(e.response.data.error);
    }
  }
);

export const signInGoogle = createAsyncThunk(
  'auth/signInGoogle',
  async (
    { googleIdToken, navigate }: { googleIdToken: any; navigate: any },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await signInGoogleAsync({
        googleIdToken,
      });

      const decodedToken = decodeJwt(data.tokens.accessToken);

      if (!decodedToken.isRegistrationComplete) {
        navigate('/auth/google-complete', { replace: true });
      } else {
        navigate('/');
      }

      setUserToLS(data.user);
      setTokensToLS(data.tokens);

      return data;
    } catch (e: any) {
      ErrorToast(e.response.data.message);
      return rejectWithValue(e.response.data.error);
    }
  }
);

export const signInGoogleComlete = createAsyncThunk(
  'auth/signInGoogleComlete',
  async (
    { user, googleUser, username, country, navigate }: GoogleUser,
    { rejectWithValue }
  ) => {
    try {
      const { data } = await signInGoogleCompleteAsync(
        user !== undefined
          ? {
              firstName: user.firstName,
              lastName: user.lastName ? user.lastName : googleUser?.lastName,
              username,
              country: country.label,
              countryCode: country.value,
            }
          : {
              firstName: googleUser?.firstName,
              lastName: googleUser?.lastName,
              username,
              country: country.label,
              countryCode: country.value,
            }
      );

      setUserToLS(data.user);
      setTokensToLS(data.tokens);
      navigate('/');
      return data;
    } catch (e: any) {
      console.log(e);
      ErrorToast(e.response.data.message);
      return rejectWithValue(e.response.data.error);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (formData: FormData, { rejectWithValue, getState }) => {
    try {
      const { data } = await updateUserProfileAsync({ formData });

      const state = getState() as RootState;

      const updatedUser = {
        ...state.authentication.user,
        firstName: data.firstName,
        lastName: data.lastName,
        userName: data.userName,
        profileImage: data.profileImage,
      };

      setUserToLS(updatedUser);

      return data;
    } catch (e: any) {
      ErrorToast(e.response.data.message);
      return rejectWithValue(e.response.data.error);
    }
  }
);

export const getUser = createAsyncThunk('users/get', async () => {
  const data = await getUserAsync();
  return data;
});

interface SendResetPasswordEmailType {
  userId: string | number;
  type: number;
  setLoading: any;
}

export const sendResetPasswordEmail = createAsyncThunk(
  'auth/sendResetPasswordEmail',
  async (
    { userId, type, setLoading }: SendResetPasswordEmailType,
    { rejectWithValue }
  ) => {
    try {
      setLoading(true);

      const { data } = await sendResetPasswordEmailAsync({
        userId,
        type,
      });

      SuccessToast(data.message);

      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      ErrorToast(e.response.data.message);
      return rejectWithValue(e.response.data.error);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (
    {
      code,
      password,
      setLoading,
      navigate,
    }: {
      code: string | null;
      password: string;
      setLoading: any;
      navigate: any;
    },
    { rejectWithValue }
  ) => {
    try {
      setLoading(true);
      const { data } = await axios.patch(`/authorization/password/reset`, {
        code,
        password,
      });

      setLoading(false);
      navigate('/auth/signin');

      SuccessToast(data.message);
    } catch (error: any) {
      setLoading(false);
      ErrorToast(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);
